<template>
  <v-dialog v-model="show" max-width="90vw" max-height="90vh" persistent>
    <v-card class="pdf-preview-dialog">
      <v-card-title class="pdf-preview-header">
        <div class="pdf-preview-title">PDF Preview</div>
        <div class="pdf-preview-actions">
          <v-btn color="primary" @click="handleDownload" prepend-icon="mdi-download" size="small">
            <span class="d-none d-sm-inline">Save</span>
            <span class="d-sm-none">Save</span>
          </v-btn>
          <v-btn color="info" @click="handlePrint" prepend-icon="mdi-printer" size="small">
            <span class="d-none d-sm-inline">Print</span>
            <span class="d-sm-none">Print</span>
          </v-btn>
          <v-btn @click="handleClose" prepend-icon="mdi-close" variant="outlined" size="small">
            <span class="d-none d-sm-inline">Close</span>
            <span class="d-sm-none">×</span>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text class="pa-0">
        <div class="pdf-container">
          <iframe
            v-if="pdfUrl"
            :src="pdfUrl + '#toolbar=1&navpanes=1&scrollbar=1'"
            width="100%"
            height="100%"
            class="pdf-iframe"
            frameborder="0"
            @error="handleError"
          ></iframe>
          <div v-if="!pdfUrl" class="pdf-loading">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-4">Loading PDF preview...</p>
          </div>
        </div>
        <!-- Fallback message if PDF doesn't display -->
        <div v-if="pdfUrl" class="pdf-fallback-message pa-4">
          <v-alert type="info" variant="tonal" class="mb-4">
            <v-alert-title>PDF Preview Ready</v-alert-title>
            If the PDF doesn't display above, your browser may not support inline PDF viewing.
            You can still save or print the PDF using the buttons above.
          </v-alert>
          <v-btn 
            color="primary" 
            :href="pdfUrl" 
            target="_blank" 
            prepend-icon="mdi-open-in-new"
            variant="outlined"
          >
            Open PDF in New Tab
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'PDFPreviewDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    pdfUrl: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'download', 'print', 'close', 'error'],
  setup(props, { emit }) {
    const show = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })

    const handleDownload = () => {
      emit('download')
    }

    const handlePrint = () => {
      emit('print')
    }

    const handleClose = () => {
      emit('close')
    }

    const handleError = () => {
      emit('error')
    }

    return {
      show,
      handleDownload,
      handlePrint,
      handleClose,
      handleError
    }
  }
}
</script>

<style scoped>
/* PDF Preview Dialog Styles */
.pdf-preview-dialog {
  height: 90vh;
  display: flex;
  flex-direction: column;
}

.pdf-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px !important;
}

.pdf-preview-title {
  font-size: 1.25rem;
  font-weight: 500;
  flex: 1;
  min-width: 120px;
}

.pdf-preview-actions {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  align-items: center;
}

.pdf-preview-dialog .v-card-text {
  flex: 1;
  overflow: hidden;
}

.pdf-container {
  width: 100%;
  height: 80vh;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.pdf-iframe {
  border: none;
  background: #f5f5f5;
}

.pdf-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
}

.pdf-fallback-message {
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}

/* Mobile responsive fixes for PDF preview */
@media screen and (max-width: 600px) {
  .pdf-preview-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .pdf-preview-title {
    text-align: center;
    min-width: unset;
  }
  
  .pdf-preview-actions {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .pdf-preview-actions .v-btn {
    flex: 1;
    min-width: 80px;
    max-width: 120px;
  }
}

@media screen and (max-width: 480px) {
  .pdf-preview-dialog {
    height: 95vh;
  }
  
  .pdf-container {
    height: 75vh;
  }
  
  .pdf-preview-actions .v-btn {
    min-width: 70px;
  }
}
</style>